/**
 * officeModel — a planta e as regras do escritório 3D da Sala dos Agentes (sem three).
 *
 * Tudo o que decide ONDE cada agente fica e COMO chega lá mora aqui, em funções puras:
 * a cena só desenha. Assim a regra é testável sem WebGL e o 2D e o 3D contam a mesma
 * história.
 *
 * A planta (x cresce para a direita; z cresce para a frente, na direção da câmera):
 *
 *     fundo ─ BIBLIOTECA (estantes na parede)          │ PLANEJAMENTO
 *             corredor do fundo                        │ (mesa redonda)
 *             BANCADAS — uma mesa por agente,          │
 *             em fileiras, com corredor atrás          │ CAFÉ E DESCANSO
 *     frente  de cada fileira                          │ (sofás, cafeteira)
 *                                            corredor principal
 *
 * Onde o agente fica segue o que ele está fazendo AGORA:
 *   gerando / ativo  → na própria mesa, digitando
 *   analisando       → na biblioteca, de pé diante das estantes
 *   planejando       → na mesa redonda de planejamento
 *   aguardando       → no café, esperando a vez (gate, dependência)
 *   concluído/falhou → na própria mesa, com o sinal em cima (comemora / coça a cabeça)
 *   ocioso           → no café e descanso (sentado, tomando café ou conversando em dupla);
 *                      de tempos em tempos dá uma volta. Chamado, levanta e volta à mesa.
 *                      Na sala GERAL quem decide é `lugarNaSalaGeral`: só fica na bancada
 *                      quem está trabalhando ou acabou de concluir (até 1 minuto).
 *
 * Quando um agente entra em cena, quem lhe passou a tarefa caminha até a mesa dele e os dois
 * ficam frente a frente por alguns segundos (`visitaDeDelegacao`).
 *
 * Nenhum agente fica de fora: as fileiras de mesas e as vagas das outras áreas crescem
 * com o tamanho do squad (o escritório 2D sentava só os 7 primeiros).
 */

/** Mesas por fileira: no mínimo 4; com squad grande a sala cresce para os lados, não só
 *  para o fundo (26 agentes em 4 colunas davam 7 fileiras e uma sala comprida demais). */
export const MESAS_POR_FILEIRA = 4;
export function mesasPorFileira(n) {
  return Math.max(MESAS_POR_FILEIRA, Math.min(8, Math.ceil(Math.sqrt(Math.max(1, n) * 1.6))));
}
/** Distância entre mesas (x) e entre fileiras (z). */
export const PASSO_X = 3.5;
export const PASSO_Z = 4.1;
/** Do centro da mesa até o assento do agente e até a faixa de circulação atrás dele. */
export const RECUO_DO_ASSENTO = 1.05;
export const RECUO_DA_FAIXA = 1.9;
/** Velocidade de caminhada (unidades por segundo). */
export const VELOCIDADE = 2.6;
/** Quanto tempo o agente fica na mesa do colega a quem passou a tarefa (segundos). */
export const DURACAO_DA_VISITA = 6;
/** Tempo de reação antes de sair do lugar (levanta, olha para onde vai) e ao chegar na mesa. */
export const TEMPO_DE_REACAO = 0.9;
export const TEMPO_DE_CHEGADA = 0.5;
/** Quanto dura cada parada do passeio de quem está sem tarefa, e o sinal de comemoração. */
export const DURACAO_DA_PARADA = 4.5;
export const DURACAO_DA_COMEMORACAO = 4;
/** O orquestrador: quem passa a tarefa quando o agente não tem um superior declarado. */
export const ORQUESTRADOR = 'tech-lead-agent';

export const ROTULO_DO_STATUS = {
  active: 'ativo', typing: 'gerando', reading: 'analisando', thinking: 'planejando',
  waiting: 'aguardando', failed: 'falhou', completed: 'concluído', idle: 'ocioso',
};

/** Cor de cada estado (hex — a cena e a legenda usam a mesma). */
export const COR_DO_STATUS = {
  active: '#5fe3ff', typing: '#5fe3ff', reading: '#7fa8ff', thinking: '#a98bff',
  waiting: '#ffbe4d', failed: '#ff5d7a', completed: '#4ade80', idle: '#7c8aa5',
};

/** Cores dos personagens — uma por agente, estável pelo identificador. */
export const CORES_DOS_AGENTES = [
  '#3ecf8e', '#4e7fff', '#e6a701', '#a855f7', '#ff8a3d', '#22c1c3', '#ef5da8', '#8bd450',
  '#5b8def', '#f2c14e', '#c084fc', '#fb7185',
];

export const ZONAS = {
  bancadas: { nome: 'Bancadas', descricao: 'gerando, concluído ou com falha' },
  biblioteca: { nome: 'Biblioteca', descricao: 'analisando' },
  planejamento: { nome: 'Planejamento', descricao: 'planejando' },
  cafe: { nome: 'Café e descanso', descricao: 'aguardando ou sem tarefa' },
};

function _hash(texto) {
  let h = 0;
  for (const ch of String(texto ?? '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

/** A cor do personagem: a mesma em toda renderização, para o mesmo agente. */
export function corDoAgente(agentId) {
  return CORES_DOS_AGENTES[_hash(agentId) % CORES_DOS_AGENTES.length];
}

/** Iniciais para a plaquinha da mesa. */
export function iniciais(nome) {
  return String(nome || '?').split(/[\s\-_]+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase()).join('') || '?';
}

/**
 * Em que área o agente está, pelo estado. Numa execução (`atuou` informado) quem está sem
 * tarefa vai para o café e descanso — tenha atuado ou não — e volta à mesa quando é chamado.
 * Sem a informação (sala geral, fora de uma execução) o ocioso fica na própria mesa: lá todos
 * estão ociosos e o café ficaria lotado com as bancadas vazias.
 */
export function zonaDoAgente(a) {
  // a tela pode dizer a área (sala geral: `lugarNaSalaGeral`); senão vale o estado
  if (a?.zona && ZONAS[a.zona]) return a.zona;
  switch (a?.status) {
    case 'reading': return 'biblioteca';
    case 'thinking': return 'planejamento';
    case 'waiting': return 'cafe';
    case 'idle': return typeof a?.atuou === 'boolean' ? 'cafe' : 'bancadas';
    default: return 'bancadas';
  }
}

/** Depois de quanto tempo quem concluiu a atividade sai da bancada (sala geral). */
export const DESCANSO_APOS_CONCLUIR_MS = 60_000;

/**
 * SALA GERAL (fora de uma execução): onde cada agente fica, pela regra do dono (2026-10-09) —
 * ninguém sentado na bancada sem trabalho.
 *
 *   trabalhando                        → na bancada;
 *   concluiu ou falhou há até 1 minuto → na bancada (comemora / coça a cabeça);
 *   concluiu ou falhou há mais tempo   → café e descanso, e sai para caminhar;
 *   aguardando ou ocioso               → mesa de planejamento ou biblioteca, "ao acaso"
 *                                        (sorteio estável por agente: ele não troca de lugar
 *                                        a cada atualização da tela).
 *
 * `a` = `{ agentId, active, execStatus, occurredAt }`. Devolve o que o escritório lê:
 * `{ status, zona?, atuou? }`.
 */
export function lugarNaSalaGeral(a, agora = Date.now()) {
  if (a?.active) return { status: 'typing' };
  const es = String(a?.execStatus || '').toLowerCase();
  const terminou = ['completed', 'done', 'failed', 'sla_expired', 'cancelled'].includes(es);
  if (terminou) {
    const quando = a?.occurredAt ? new Date(a.occurredAt).getTime() : NaN;
    const recente = Number.isFinite(quando) && agora - quando <= DESCANSO_APOS_CONCLUIR_MS;
    if (recente && es !== 'cancelled') {
      return { status: es === 'failed' || es === 'sla_expired' ? 'failed' : 'completed' };
    }
    return { status: 'idle', zona: 'cafe', atuou: true };
  }
  const aguardando = ['gate_waiting', 'gate-waiting', 'pending', 'queued', 'waiting'].includes(es);
  return {
    status: aguardando ? 'waiting' : 'idle',
    zona: _hash(`lugar:${a?.agentId ?? ''}`) % 2 === 0 ? 'planejamento' : 'biblioteca',
  };
}

/** A pose do personagem no destino. */
export function poseDoAgente(a) {
  switch (a?.status) {
    case 'typing': case 'active': return 'digitando';
    case 'reading': return 'lendo';
    case 'thinking': return 'pensando';
    case 'waiting': case 'idle': {
      // quem espera ou está sem tarefa na biblioteca folheia; na mesa redonda, pensa
      const zona = zonaDoAgente(a);
      if (a?.zona && zona === 'biblioteca') return 'lendo';
      if (a?.zona && zona === 'planejamento') return 'pensando';
      if (a.status === 'waiting') return 'esperando';
      return zona === 'cafe' ? 'descansando' : 'sentado';
    }
    case 'completed': return 'concluido';
    case 'failed': return 'falhou';
    default: return zonaDoAgente(a) === 'cafe' ? 'descansando' : 'sentado';
  }
}

/**
 * O gesto que o estado pede, por cima da pose: comemorar ao concluir, coçar a cabeça ao
 * falhar e levantar a mão ao pedir esclarecimento no gate.
 */
export function sinalDoAgente(a) {
  if (a?.status === 'completed') return 'comemorar';
  if (a?.status === 'failed') return 'cocar';
  if (a?.status === 'waiting'
      && /esclarec|d[uú]vida|pergunta|clarif/i.test(String(a?.activity || ''))) {
    return 'mao_levantada';
  }
  return null;
}

/**
 * Quem vai até a mesa de quem: o agente ao vivo recebe a visita de quem lhe passou a tarefa
 * — o superior declarado (`parentAgentId`) ou, sem ele, o orquestrador.
 * @returns {{de: string, para: string}|null}
 */
export function visitaDeDelegacao(agents) {
  const lista = agents ?? [];
  const vivo = lista.find((a) => a.active);
  if (!vivo || zonaDoAgente(vivo) !== 'bancadas') return null;
  const de = vivo.parentAgentId || (vivo.agentId !== ORQUESTRADOR ? ORQUESTRADOR : null);
  if (!de || de === vivo.agentId || !lista.some((a) => a.agentId === de)) return null;
  return { de, para: vivo.agentId };
}

/** O que o balão diz: a atividade real ou, sem ela, o estado em palavras. */
export function falaDoAgente(a, teto = 64) {
  const bruto = String(a?.activity || '').trim() || {
    waiting: 'Aguardando a vez…', thinking: 'Planejando as tarefas…',
    reading: 'Analisando o material…', typing: 'Gerando código…', active: 'Trabalhando…',
    completed: 'Concluído', failed: 'Falhou',
  }[a?.status] || '';
  return bruto.length > teto ? `${bruto.slice(0, teto - 1).trimEnd()}…` : bruto;
}

/** A ordem ESTÁVEL das mesas: não muda quando o agente ativo muda. */
export function ordemDasMesas(agents) {
  return [...(agents ?? [])].sort((x, y) => {
    const ox = x.agentId === 'tech-lead-agent' ? 0 : 1;
    const oy = y.agentId === 'tech-lead-agent' ? 0 : 1;
    if (ox !== oy) return ox - oy;
    if (Boolean(x.isSubagent) !== Boolean(y.isSubagent)) return x.isSubagent ? 1 : -1;
    return String(x.name || x.agentId).localeCompare(String(y.name || y.agentId), 'pt-BR')
      || String(x.agentId).localeCompare(String(y.agentId));
  });
}

/**
 * A planta para `n` agentes: dimensões, mesas e pontos fixos das áreas.
 * As mesas ficam centradas no bloco; o corredor principal fica à direita dele.
 */
export function plantaDoEscritorio(n) {
  const total = Math.max(1, n | 0);
  const porFileira = mesasPorFileira(total);
  const colunas = Math.min(porFileira, total);
  const fileiras = Math.ceil(total / porFileira);
  const larguraDoBloco = (colunas - 1) * PASSO_X;
  const x0 = -larguraDoBloco / 2 - 2.0;          // o bloco fica um pouco à esquerda
  const z0 = -((fileiras - 1) * PASSO_Z) / 2;    // primeira fileira (fundo)

  const mesas = [];
  for (let i = 0; i < total; i += 1) {
    const col = i % porFileira;
    const fil = Math.floor(i / porFileira);
    const x = x0 + col * PASSO_X;
    const z = z0 + fil * PASSO_Z;
    mesas.push({
      indice: i, fileira: fil, coluna: col,
      mesa: { x, z },
      assento: { x, z: z + RECUO_DO_ASSENTO },
      faixa: { x, z: z + RECUO_DA_FAIXA },
    });
  }

  const xDireitaDoBloco = x0 + larguraDoBloco;
  const xCorredor = xDireitaDoBloco + 2.6;
  const xLateral = xCorredor + 6.0;               // centro das áreas à direita
  const zFundoDoBloco = z0 - 1.2;
  const zFrenteDoBloco = z0 + (fileiras - 1) * PASSO_Z + RECUO_DA_FAIXA + 0.8;
  const zFundo = Math.min(zFundoDoBloco - 2.6, -8.2);
  const zFrente = Math.max(zFrenteDoBloco, 8.2);
  const xEsquerda = x0 - 3.4;
  const xDireita = xLateral + 6.0;
  const zCafe = zFrente - 3.8;
  const xBalcao = xDireita - 0.55;                // cafeteira e geladeira, na parede direita

  return {
    total, colunas, fileiras, mesas,
    limites: { xMin: xEsquerda, xMax: xDireita, zMin: zFundo - 1.4, zMax: zFrente + 0.6 },
    corredor: { x: xCorredor },
    corredorDoFundo: { z: zFundo + 1.5 },
    biblioteca: { x0, x1: xDireitaDoBloco, zParede: zFundo - 0.6, zFila: zFundo + 0.9 },
    planejamento: { x: xLateral, z: zFundo + 2.8, raio: 1.75 },
    cafe: {
      x: xLateral, z: zCafe,
      faixa: zCafe - 0.9,                          // por onde se circula dentro do café
      faixaDoFundo: zCafe - 2.45,
      sofa: { x: xLateral, z: zCafe + 2.3 },
      mesinha: { x: xLateral, z: zCafe + 0.8 },
      poltronas: [{ x: xLateral - 2.9, z: zCafe + 0.9 }, { x: xLateral + 2.9, z: zCafe + 0.9 }],
      cafeteira: { x: xBalcao, z: zCafe - 1.9 },
      geladeira: { x: xBalcao, z: zCafe - 0.4 },
      planta: { x: xBalcao, z: zCafe + 2.6 },
    },
  };
}

function _vagasEmArco(centro, raio, quantas) {
  // em volta de um ponto, olhando para o centro
  const n = Math.max(quantas, 1);
  return Array.from({ length: n }, (_v, i) => {
    const ang = (i / Math.max(n, 5)) * Math.PI * 2 + Math.PI / 5;
    const r = raio + Math.floor(i / 8) * 0.9;
    return { x: centro.x + Math.cos(ang) * r, z: centro.z + Math.sin(ang) * r,
             olhar: { x: centro.x, z: centro.z } };
  });
}

function _vagasEmFila(x0, x1, z, quantas, olharZ) {
  const n = Math.max(quantas, 1);
  const passo = Math.max(1.15, Math.min(1.9, (x1 - x0) / Math.max(n - 1, 1)));
  const largura = (n - 1) * passo;
  const inicio = (x0 + x1) / 2 - largura / 2;
  return Array.from({ length: n }, (_v, i) => {
    const x = inicio + i * passo;
    return { x, z, olhar: { x, z: olharZ } };
  });
}

/**
 * As vagas do café para `quantas` pessoas, na ordem em que são ocupadas — misturando quem
 * senta (sofá, poltronas), quem toma café de pé na cafeteira e quem conversa em dupla.
 * Cada vaga traz a pose, para onde olha, o ponto de acesso (na faixa de circulação) e os
 * pontos de `entrada` entre o acesso e o lugar: é por eles que se contorna a mesinha.
 * Dupla só existe completa: sobrando um, ele ganha outra vaga.
 */
export function vagasDoCafe(planta, quantas) {
  const c = planta.cafe;
  const noBalcao = c.cafeteira.x - 1.05;
  const sentado = (x, z, lado) => ({
    pose: 'descansando', pos: { x, z }, olhar: { ...c.mesinha },
    acesso: { x: c.x + lado * 1.4, z: c.faixa },
    entrada: [{ x: c.x + lado * 1.4, z: c.z + 1.6 }],
  });
  const dePe = (pose, x, z, olhar, faixa = c.faixa) => ({
    pose, pos: { x, z }, olhar, acesso: { x, z: faixa }, entrada: [],
  });
  const dupla = (cx) => {
    const z = c.z - 1.9;
    return [dePe('conversando', cx - 0.5, z, { x: cx + 0.5, z }),
            dePe('conversando', cx + 0.5, z, { x: cx - 0.5, z })];
  };
  const sozinhas = [
    sentado(c.sofa.x - 0.7, c.sofa.z - 0.1, -1),
    dePe('bebendo', noBalcao, c.cafeteira.z, { ...c.cafeteira }),
    sentado(c.poltronas[0].x, c.poltronas[0].z, -1),
    sentado(c.sofa.x + 0.7, c.sofa.z - 0.1, 1),
    sentado(c.poltronas[1].x, c.poltronas[1].z, 1),
  ];
  const duplas = [dupla(c.x - 3.3), dupla(c.x - 0.8), dupla(c.x + 1.7)];
  // de pé, em fileiras atrás das duplas: para o squad grande que parou inteiro
  const sobra = (i) => {
    const col = i % 7;
    const fil = Math.floor(i / 7);
    const x = c.x - 3.6 + col * 1.2;
    const z = c.z - 3.0 - fil * 1.1;
    return dePe('em_pe', x, z, { x, z: c.z }, c.faixaDoFundo - fil * 1.1);
  };

  const n = Math.max(0, quantas | 0);
  const vagas = [];
  let s = 0; let d = 0; let extra = 0;
  // ciclo: dois sentados/no balcão, depois uma dupla — enquanto houver gente para a dupla inteira
  while (vagas.length < n) {
    for (let k = 0; k < 2 && s < sozinhas.length && vagas.length < n; k += 1) {
      vagas.push(sozinhas[s]);
      s += 1;
    }
    if (vagas.length >= n) break;
    if (d < duplas.length && n - vagas.length >= 2) {
      vagas.push(...duplas[d]);
      d += 1;
    } else if (s >= sozinhas.length) {
      vagas.push(sobra(extra));
      extra += 1;
    }
  }
  return vagas;
}

/** Onde quem está sem tarefa para no passeio: cafeteira, geladeira e a vista da sala. */
export function paradasDoPasseio(planta) {
  const c = planta.cafe;
  const noBalcao = c.cafeteira.x - 1.05;
  return [
    { pos: { x: noBalcao, z: c.cafeteira.z + 0.9 }, olhar: { ...c.cafeteira }, pose: 'bebendo' },
    { pos: { x: noBalcao, z: c.geladeira.z + 0.2 }, olhar: { ...c.geladeira }, pose: 'em_pe' },
    { pos: { x: c.x - 4.4, z: c.z + 1.9 }, olhar: { x: c.x - 9, z: c.z + 1.9 }, pose: 'em_pe' },
  ].map((p) => ({ ...p, acesso: { x: p.pos.x, z: c.faixa }, entrada: [] }));
}

/**
 * O passeio de quem está sem tarefa: do lugar até uma parada e de volta, sempre pela faixa
 * de circulação do café. `vez` escolhe a parada (cada volta vai a uma diferente).
 * @returns {{parada, ida: Array<{x,z}>, volta: Array<{x,z}>}|null}
 */
export function passeioNoCafe(planta, lugar, vez = 0) {
  if (lugar?.zona !== 'cafe' || lugar.pose === 'conversando') return null;
  const paradas = paradasDoPasseio(planta);
  const parada = paradas[Math.abs(vez | 0) % paradas.length];
  const saida = [...(lugar.entrada ?? [])].reverse().concat([lugar.acesso]);
  const ida = rota(lugar.pos, saida, parada, planta);
  const volta = rota(parada.pos, [parada.acesso], lugar, planta);
  return { parada, ida, volta };
}

/**
 * Para onde vai cada agente AGORA.
 * @returns {{planta, lugares: Record<string, {zona, pose, pos, olhar, acesso, entrada, mesa,
 *   sinal, visita?, visitante?}>, porZona, visita}}
 */
export function montarEscritorio(agents) {
  const lista = agents ?? [];
  const ordem = ordemDasMesas(lista);
  const planta = plantaDoEscritorio(ordem.length);
  const mesaDe = new Map(ordem.map((a, i) => [a.agentId, planta.mesas[i]]));

  const porZona = { bancadas: [], biblioteca: [], planejamento: [], cafe: [] };
  for (const a of ordem) porZona[zonaDoAgente(a)].push(a);

  const vagasBiblioteca = _vagasEmFila(planta.biblioteca.x0, planta.biblioteca.x1,
    planta.biblioteca.zFila, porZona.biblioteca.length, planta.biblioteca.zParede);
  const vagasPlanejamento = _vagasEmArco(planta.planejamento, planta.planejamento.raio,
    porZona.planejamento.length);
  // a vaga do café é FIXA por agente (pela ordem das mesas), como a mesa: quando um colega
  // sai para trabalhar ninguém troca de lugar — cada um volta sempre para o seu canto
  const vagasCafe = vagasDoCafe(planta, ordem.length);
  const noCafe = new Set(porZona.cafe.map((a) => a.agentId));
  const _chave = (p) => `${p.x.toFixed(2)}:${p.z.toFixed(2)}`;
  const ocupadas = new Set(ordem.filter((a) => noCafe.has(a.agentId))
    .map((a) => _chave(vagasCafe[ordem.indexOf(a)].pos)));

  const lugares = {};
  for (const a of ordem) {
    const zona = zonaDoAgente(a);
    const mesa = mesaDe.get(a.agentId);
    let pos; let olhar; let acesso; let entrada = [];
    let pose = poseDoAgente(a);
    if (zona === 'bancadas') {
      pos = { ...mesa.assento };
      olhar = { ...mesa.mesa };
      acesso = { ...mesa.faixa };
    } else if (zona === 'cafe') {
      const vaga = vagasCafe[ordem.indexOf(a)];
      ({ pos, olhar, acesso, entrada } = vaga);
      // sentado, tomando café ou em dupla — a dupla só conversa com o par presente
      pose = vaga.pose === 'conversando' && !ocupadas.has(_chave(vaga.olhar)) ? 'em_pe' : vaga.pose;
    } else {
      const vagas = { biblioteca: vagasBiblioteca, planejamento: vagasPlanejamento }[zona];
      const vaga = vagas[porZona[zona].indexOf(a)];
      pos = { x: vaga.x, z: vaga.z };
      olhar = vaga.olhar;
      acesso = { x: vaga.x, z: vaga.z };
    }
    lugares[a.agentId] = { zona, pose, pos, olhar, acesso, entrada, mesa,
                           sinal: sinalDoAgente(a) };
  }

  // quem passou a tarefa vai até a mesa de quem entrou em cena
  const visita = visitaDeDelegacao(ordem);
  if (visita) {
    const mesa = mesaDe.get(visita.para);
    lugares[visita.de].visita = {
      para: visita.para, zona: 'bancadas', pose: 'conversando',
      pos: { x: mesa.assento.x + 1.0, z: mesa.assento.z + 0.35 },
      olhar: { ...mesa.assento }, acesso: { ...mesa.faixa }, entrada: [],
    };
    lugares[visita.para].visitante = visita.de;
  }
  return { planta, ordem, lugares, porZona, visita };
}

function _perto(a, b, folga = 0.05) {
  return Math.abs(a.x - b.x) <= folga && Math.abs(a.z - b.z) <= folga;
}

/**
 * O caminho de `de` até o lugar `para`, pelos corredores (nunca por cima das mesas):
 * sai pela faixa atrás da própria fileira, anda pelo corredor principal e entra na faixa
 * (ou na área) de destino.
 * @param {{x,z}} de posição atual
 * @param {{x,z}|Array<{x,z}>|null} acessoDe por onde se sai do lugar atual: o ponto de acesso
 *   ou, quando há móveis a contornar, a lista de pontos até ele
 * @param {{pos, acesso, entrada?}} para lugar de destino (`entrada`: pontos entre o acesso e
 *   o lugar)
 */
export function rota(de, acessoDe, para, planta) {
  if (_perto(de, para.pos)) return [];
  const xC = planta.corredor.x;
  const pontos = [];
  let atual = de;
  const ir = (p) => { if (!_perto(atual, p)) { pontos.push({ x: p.x, z: p.z }); atual = p; } };
  for (const p of [].concat(acessoDe ?? [])) ir(p);
  // mesma faixa (mesma fileira): anda direto por ela
  if (!(Math.abs(atual.z - para.acesso.z) < 0.05)) {
    ir({ x: xC, z: atual.z });
    ir({ x: xC, z: para.acesso.z });
  }
  ir(para.acesso);
  for (const p of para.entrada ?? []) ir(p);
  ir(para.pos);
  return pontos;
}

/** Comprimento de um caminho a partir de `de`. */
export function comprimentoDaRota(de, pontos) {
  let total = 0;
  let p = de;
  for (const q of pontos) {
    total += Math.hypot(q.x - p.x, q.z - p.z);
    p = q;
  }
  return total;
}

/**
 * Um passo de caminhada: avança `dt` segundos pela fila de pontos.
 * @returns {{pos:{x,z}, fila:Array, andando:boolean, direcao:number|null}}
 */
export function andar(pos, fila, dt, velocidade = VELOCIDADE) {
  let resto = Math.max(0, velocidade * dt);
  let atual = { x: pos.x, z: pos.z };
  const pendentes = [...fila];
  let direcao = null;
  while (pendentes.length && resto > 0) {
    const alvo = pendentes[0];
    const dx = alvo.x - atual.x;
    const dz = alvo.z - atual.z;
    const dist = Math.hypot(dx, dz);
    if (dist <= resto + 1e-6) {
      atual = { x: alvo.x, z: alvo.z };
      resto -= dist;
      pendentes.shift();
      if (dist > 1e-4) direcao = Math.atan2(dx, dz);
    } else {
      atual = { x: atual.x + (dx / dist) * resto, z: atual.z + (dz / dist) * resto };
      direcao = Math.atan2(dx, dz);
      resto = 0;
    }
  }
  return { pos: atual, fila: pendentes, andando: pendentes.length > 0, direcao };
}

/** Ângulo (em torno de y) para olhar de `de` para `para`. */
export function anguloPara(de, para) {
  return Math.atan2(para.x - de.x, para.z - de.z);
}

/** Quantos agentes em cada estado — para a legenda. */
export function contagemPorStatus(agents) {
  const out = {};
  for (const a of agents ?? []) out[a.status || 'idle'] = (out[a.status || 'idle'] || 0) + 1;
  return out;
}
